(function () {
  if (window.StickyAddToCart) return;

  class StickyAddToCart {
    constructor(root, options) {
      if (!root) return;

      this.root = root;
      this.popover = root.querySelector('[data-sticky-atc-popover]');
      this.closeButton = root.querySelector('[data-sticky-atc-close]');
      this.trigger = root.querySelector('[data-sticky-atc-trigger]');
      this.variantInput = root.querySelector('[data-sticky-atc-variant-input]');
      this.variantButtons = Array.from(root.querySelectorAll('.sticky-atc__variant-option'));
      this.quantityInput = root.querySelector('.sticky-atc__quantity .quantity__input');
      this.form = document.getElementById(options.formId);
      this.observeTarget = document.querySelector(options.observeSelector);

      if (!this.observeTarget || !this.form || !this.trigger) return;

      // Text of the button: "Choose Your Set" while the options are closed,
      // "Add To Cart – <price of the selected variant>" while they are open.
      this.label = this.trigger.querySelector('span');
      this.closedLabel = options.closedLabel || (this.label ? this.label.textContent.trim() : '');
      this.openLabel = options.openLabel || 'Add To Cart';

      // "Total" row: price of the selected variant x quantity.
      this.totalElement = root.querySelector('[data-sticky-atc-total]');
      this.currency = options.currency || 'USD';

      this.selectedButton = null;
      this.selectInitialVariant();

      this.bindEvents();
      this.observeVisibility();
    }

    // "Add Case" (the variant with the case) is preselected; otherwise the first available variant.
    selectInitialVariant() {
      const available = this.variantButtons.filter((button) => !button.disabled);
      const preferred = available.find((button) => button.classList.contains('variant-pill--upsell')) || available[0];
      if (preferred) this.selectVariant(preferred);
    }

    selectVariant(button) {
      this.variantButtons.forEach((other) => {
        other.classList.toggle('is-selected', other === button);
        other.setAttribute('aria-pressed', other === button ? 'true' : 'false');
      });
      this.selectedButton = button;
      this.variantInput.value = button.getAttribute('data-variant-id');
      if (this.isOpen()) this.updateLabel();
      this.updateTotal();
    }

    // Total = price of the selected variant x quantity, as "A$79.90" (same format as the price in
    // the add-to-cart button on the page). Returns '' if there is no price.
    getTotalText() {
      if (!this.selectedButton) return '';
      const cents = Number(this.selectedButton.getAttribute('data-variant-price-cents'));
      const quantity = Math.max(1, parseInt(this.quantityInput ? this.quantityInput.value : '1', 10) || 1);
      if (!cents) return '';
      const total = (cents * quantity) / 100;
      let formatted;
      try {
        // narrowSymbol: "$79.90" (without it, AUD would be written "A$79.90" and get a second "A" below)
        formatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.currency,
          currencyDisplay: 'narrowSymbol'
        }).format(total);
      } catch (error) {
        formatted = '$' + total.toFixed(2);
      }
      return 'A' + formatted;
    }

    // Kept for the old "Total" row (no longer in the markup) and used to refresh the button text.
    updateTotal() {
      if (this.totalElement) this.totalElement.textContent = this.getTotalText();
      if (this.isOpen()) this.updateLabel();
    }

    // Button text: "Choose Your Set" while the options are closed. While they are open:
    // "Add To Cart" followed by the total in its own <span>; the separator in front of the price
    // (a dash on desktop, only a space on mobile) comes from the CSS (.sticky-atc__button-price::before).
    updateLabel() {
      if (!this.label) return;
      this.label.textContent = '';
      if (this.isOpen() && this.selectedButton) {
        this.label.appendChild(document.createTextNode(this.openLabel));
        const total = this.getTotalText();
        if (total) {
          const price = document.createElement('span');
          price.className = 'sticky-atc__button-price';
          price.textContent = total;
          this.label.appendChild(price);
        }
      } else {
        this.label.textContent = this.closedLabel;
      }
    }

    isOpen() {
      return !!this.popover && this.popover.classList.contains('is-open');
    }

    bindEvents() {
      this.trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.variantButtons.length <= 1) {
          this.submit();
          return;
        }
        // First click opens the options, the next click adds the selected variant to the cart.
        if (this.isOpen()) {
          this.submit();
          this.closePopover();
        } else {
          this.openPopover();
        }
      });

      this.variantButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          this.selectVariant(button);
        });
      });

      this.quantityInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      });

      // Keep the total in sync when the quantity changes (typing, plus/minus buttons).
      this.quantityInput?.addEventListener('change', () => this.updateTotal());
      this.quantityInput?.addEventListener('input', () => this.updateTotal());

      this.closeButton?.addEventListener('click', () => this.closePopover());

      document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target)) this.closePopover();
      });
    }

    openPopover() {
      if (!this.popover) return;
      this.popover.classList.add('is-open');
      this.popover.setAttribute('aria-hidden', 'false');
      this.root.classList.add('is-popover-open'); // shows the "Total" row
      this.updateLabel();
    }

    closePopover() {
      if (!this.popover) return;
      this.popover.classList.remove('is-open');
      this.popover.setAttribute('aria-hidden', 'true');
      this.root.classList.remove('is-popover-open'); // hides the "Total" row
      this.updateLabel();
    }

    submit() {
      this.form.requestSubmit();
    }

    observeVisibility() {
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            this.hide();
          } else if (entry.boundingClientRect.top < 0) {
            this.show();
          }
        },
        { threshold: 0 }
      );
      observer.observe(this.observeTarget);
    }

    show() {
      this.root.removeAttribute('hidden');
    }

    hide() {
      this.root.setAttribute('hidden', '');
      this.closePopover();
    }
  }

  window.StickyAddToCart = StickyAddToCart;
})();
